import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Status } from '@/components';
import {
  showCaughtErrorToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { EscalationStatus } from '@/types/rental';
import {
  getRentalTrackingDetail,
  RentalTrackingDetail,
  RentalTrackingItem,
} from '@/services/api/rentTracking';
import { VehicleTrailPoint } from '@/services/api/vehicleTelemetry';
import { formatDateTime, formatRupiah } from '@/utils/format';
import {
  buildUnitClipboardText,
  clampTrailRange,
  SPEED_STOPS,
  summariseTrail,
  TRAIL_MAX_RANGE_DAYS,
  TRAIL_PRESETS,
  TrailRange,
} from '../rentTrackingDisplay';
import { UnitDetailBlocks } from './UnitDetailBlocks';

// What <input type="datetime-local"> wants: local wall-clock, no zone.
const toLocalInput = (d: Date): string => dayjs(d).format('YYYY-MM-DDTHH:mm');

export interface UnitTrailPanelProps {
  item: RentalTrackingItem;
  trail: VehicleTrailPoint[] | null;
  isLoadingTrail: boolean;
  onLoadTrail: (range: TrailRange) => void;
  onClearTrail: () => void;
  cursorMs: number | null;
  onCursorChange: (cursorMs: number | null) => void;
  onClose: () => void;
}

/**
 * The selected unit in full, plus the movement history controls. The detail is
 * fetched per unit; until it lands the list's own row is shown, so clicking a
 * dot is never a blank panel.
 */
export const UnitTrailPanel: React.FC<UnitTrailPanelProps> = ({
  item,
  trail,
  isLoadingTrail,
  onLoadTrail,
  onClearTrail,
  cursorMs,
  onCursorChange,
  onClose,
}) => {
  const navigate = useNavigate();
  const [range, setRange] = useState<TrailRange>(() =>
    TRAIL_PRESETS[0].range()
  );
  const [activePreset, setActivePreset] = useState<string | null>('today');
  const [detail, setDetail] = useState<RentalTrackingDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // A new unit = a fresh range; the parent already dropped the old trail.
  useEffect(() => {
    setRange(TRAIL_PRESETS[0].range());
    setActivePreset('today');
  }, [item.rentalID]);

  const loadDetail = useCallback(async (rentalID: string) => {
    setIsLoadingDetail(true);
    try {
      setDetail(await getRentalTrackingDetail(rentalID));
    } catch (error) {
      // The list row still renders below, so say it once and let the user
      // retry.
      setDetail(null);
      showCaughtErrorToast(error, 'Gagal memuat detail unit');
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    // Clicking through dots faster than the API answers must not paint one
    // unit's contacts under another's header.
    let current = true;
    setDetail(null);
    setIsLoadingDetail(true);
    getRentalTrackingDetail(item.rentalID)
      .then((result) => {
        if (current) setDetail(result);
      })
      .catch((error) => {
        if (current) showCaughtErrorToast(error, 'Gagal memuat detail unit');
      })
      .finally(() => {
        if (current) setIsLoadingDetail(false);
      });
    return () => {
      current = false;
    };
  }, [item.rentalID]);

  const [copied, setCopied] = useState(false);
  const copySummary = async () => {
    // Only https and localhost expose the clipboard, so say why rather than
    // failing silently on an http preview.
    if (!navigator.clipboard) {
      showErrorToast(
        'Tidak bisa menyalin',
        'Browser hanya mengizinkan clipboard di halaman HTTPS.'
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(buildUnitClipboardText(item, detail));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menyalin data unit');
    }
  };

  const summary = useMemo(
    () => (trail ? summariseTrail(trail) : null),
    [trail]
  );
  const firstMs = trail?.[0]?.positioningMs ?? 0;
  const lastMs = trail?.[trail.length - 1]?.positioningMs ?? 0;

  const applyRange = (next: TrailRange, preset: string | null) => {
    setRange(clampTrailRange(next));
    setActivePreset(preset);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* pr/pb leave room for the card's own shadow inside the scroller. */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-0.5 pr-0.5">
        <div className="rounded-xl border border-gray-200 bg-white shadow-lg flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
            <div>
              {/* Plate only: the mitra and the rental code have their
                  own rows under Informasi mitra and Informasi rental. */}
              <p className="text-[16px] font-semibold text-gray-900">
                {item.platNomor ?? '-'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {item.escalationStatus !== EscalationStatus.NORMAL && (
                <Status
                  value={item.escalationStatus}
                  type="escalationStatus"
                  size="small"
                />
              )}
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Tutup panel"
              >
                <i className="ri-close-line text-[18px]"></i>
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {detail ? (
              <UnitDetailBlocks detail={detail} />
            ) : (
              <>
                {/* What the list already knows, so the panel is never blank. */}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px]">
                  <dt className="text-gray-500">Tunggakan</dt>
                  <dd className="text-gray-900 text-right">
                    {formatRupiah(item.arrears.amount)} ({item.arrears.days}{' '}
                    hari)
                  </dd>
                  <dt className="text-gray-500">Fix terakhir</dt>
                  <dd className="text-gray-900 text-right">
                    {item.position?.lastAt
                      ? formatDateTime(item.position.lastAt)
                      : '-'}
                  </dd>
                  <dt className="text-gray-500">Hub</dt>
                  <dd className="text-gray-900 text-right">
                    {item.hubName ?? '-'}
                  </dd>
                  <dt className="text-gray-500">Case repo</dt>
                  <dd className="text-right">
                    {item.repossession ? (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() =>
                          navigate(`/repossessions/${item.repossession!.ID}`)
                        }
                      >
                        {item.repossession.code} · {item.repossession.status}
                      </button>
                    ) : (
                      '-'
                    )}
                  </dd>
                </dl>
                {isLoadingDetail ? (
                  <p className="text-[12px] text-gray-500">
                    Memuat detail dan kontak…
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => loadDetail(item.rentalID)}
                    className="text-[12px] text-primary hover:underline"
                  >
                    Muat ulang detail dan kontak
                  </button>
                )}
              </>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="small" onClick={copySummary}>
                <i
                  className={`${
                    copied ? 'ri-check-line' : 'ri-file-copy-line'
                  } text-[12px] mr-1`}
                ></i>
                {copied ? 'Tersalin' : 'Salin data'}
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate(`/rent-activity/${item.rentalID}`)}
              >
                Buka rental
              </Button>
              {item.vehicleID && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => navigate(`/vehicles/${item.vehicleID}`)}
                >
                  Detail unit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex max-h-[55%] flex-shrink-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
          <p className="text-[14px] font-medium text-gray-900">
            Riwayat pergerakan
          </p>
          <p className="text-[12px] text-gray-500">
            Jejak GPS unit ini pada rentang waktu yang kamu pilih.
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {!item.vehicleID ? (
            <p className="text-[13px] text-gray-500">
              Rental ini belum punya unit, tidak ada jejak.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {TRAIL_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => applyRange(preset.range(), preset.key)}
                    className={`px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors ${
                      activePreset === preset.key
                        ? 'bg-[#5e2aac] text-white border-[#5e2aac]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <label className="text-[12px] text-gray-500">
                  Dari
                  <input
                    type="datetime-local"
                    value={toLocalInput(range.from)}
                    max={toLocalInput(range.to)}
                    onChange={(e) =>
                      e.target.value &&
                      applyRange(
                        { ...range, from: new Date(e.target.value) },
                        null
                      )
                    }
                    className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] text-gray-900"
                  />
                </label>
                <label className="text-[12px] text-gray-500">
                  Sampai
                  <input
                    type="datetime-local"
                    value={toLocalInput(range.to)}
                    min={toLocalInput(range.from)}
                    onChange={(e) =>
                      e.target.value &&
                      applyRange(
                        { ...range, to: new Date(e.target.value) },
                        null
                      )
                    }
                    className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] text-gray-900"
                  />
                </label>
              </div>
              <p className="text-[12px] text-gray-400 mb-2">
                Maksimal {TRAIL_MAX_RANGE_DAYS} hari per pencarian.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => onLoadTrail(range)}
                  disabled={isLoadingTrail}
                >
                  <i className="ri-route-line text-[12px] mr-1"></i>
                  {isLoadingTrail ? 'Memuat…' : 'Muat jejak'}
                </Button>
                {trail && (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={onClearTrail}
                  >
                    Hapus jejak
                  </Button>
                )}
              </div>
            </>
          )}

          {trail && summary && (
            <div className="mt-3 space-y-2">
              {trail.length === 0 ? (
                <p className="text-[13px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  Tidak ada titik telemetry di rentang ini.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[
                      { label: 'Titik', value: summary.points },
                      { label: 'Bergerak', value: summary.moving },
                      {
                        label: 'Maks km/j',
                        value: Math.round(summary.maxSpeed),
                      },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className="rounded-lg bg-gray-50 px-2 py-1.5"
                      >
                        <p className="text-[11px] text-gray-500">
                          {stat.label}
                        </p>
                        <p className="text-[14px] font-semibold text-gray-900">
                          {stat.value}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[12px] text-gray-500 mb-1">
                      <span>{formatDateTime(trail[0].positioningTime)}</span>
                      <span className="font-medium text-gray-900">
                        {cursorMs === null
                          ? 'Seluruh jejak'
                          : formatDateTime(new Date(cursorMs).toISOString())}
                      </span>
                      <span>
                        {formatDateTime(
                          trail[trail.length - 1].positioningTime
                        )}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={firstMs}
                      max={lastMs}
                      step={60_000}
                      value={cursorMs ?? lastMs}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        onCursorChange(v >= lastMs ? null : v);
                      }}
                      className="w-full accent-[#5e2aac]"
                      aria-label="Geser waktu jejak"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SPEED_STOPS.map((stop) => (
                      <span
                        key={stop.label}
                        className="inline-flex items-center gap-1 text-[11px] text-gray-600"
                      >
                        <span
                          className="inline-block w-3 h-1.5 rounded"
                          style={{ backgroundColor: stop.color }}
                        />
                        {stop.label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitTrailPanel;
